import { useCallback, useEffect, useState } from "react";
import { Users, Eye, Clock, MousePointerClick, CalendarCheck, Radio, ChevronLeft, ChevronRight, Monitor, Smartphone, Tablet } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, formatApiError } from "@/lib/api";
import { fmtDateShort, fmtTime } from "@/lib/format";
import { PageHeader } from "@/components/admin/StatusBadge";

const RANGES = [["1", "Today"], ["7", "7 days"], ["30", "30 days"], ["90", "90 days"]];
const SECTION_LABELS = { learn: "Learn", how: "How it works", about: "About", booking: "Booking" };
const DEVICE_ICON = { Mobile: Smartphone, Tablet, Desktop: Monitor };
const PAGE_SIZE = 20;

export const fmtDuration = (s) => (s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, "0")}s`);
const flag = (cc) => (cc && cc.length === 2 ? String.fromCodePoint(...[...cc.toUpperCase()].map((c) => 127397 + c.charCodeAt(0))) : "");

function TopList({ title, rows, tid, onPick }) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="glass rounded-3xl p-5" data-testid={tid}>
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#697386]">{title}</p>
      <ul className="mt-4 space-y-3">
        {rows.length === 0 && <li className="text-sm text-[#697386]">No data yet.</li>}
        {rows.map((r) => (
          <li key={r.label}>
            <button onClick={() => onPick?.(r.label)} disabled={!onPick} className="w-full text-left">
              <div className="flex justify-between text-sm"><span className="text-[#172033] truncate">{r.label}</span><span className="text-[#697386]">{r.count}</span></div>
              <div className="mt-1 h-1.5 rounded-full bg-[#0B1F3A]/5"><div className="h-full rounded-full bg-[#C7A76C]" style={{ width: `${(r.count / max) * 100}%` }} /></div>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Stats({ s }) {
  const cards = [
    ["unique_visitors", "Unique visitors", Users, s?.unique_visitors],
    ["sessions", "Visits", Eye, s?.sessions],
    ["avg", "Avg. time on site", Clock, s ? fmtDuration(s.avg_seconds) : null],
    ["started", "Opened booking form", MousePointerClick, s?.booking_started],
    ["booked", "Booked", CalendarCheck, s?.booked],
    ["online", "Online now", Radio, s?.online_now],
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
      {cards.map(([k, label, Icon, v]) => (
        <div key={k} className="glass rounded-3xl p-5" data-testid={`visitor-stat-${k}`}>
          <Icon size={18} className={k === "online" && v ? "text-emerald-500" : "text-[#C7A76C]"} strokeWidth={1.75} />
          <p className="mt-5 font-display text-2xl font-bold text-[#0B1F3A]" data-testid={`visitor-stat-value-${k}`}>{v ?? "–"}</p>
          <p className="mt-1 text-xs text-[#697386]">{label}</p>
        </div>
      ))}
    </div>
  );
}

function VisitRow({ v }) {
  const Icon = DEVICE_ICON[v.device] || Monitor;
  return (
    <tr className="border-b border-[rgba(20,35,55,0.06)]" data-testid={`visit-row-${v.session_id}`}>
      <td className="px-4 py-3.5 whitespace-nowrap"><p className="text-[#172033]">{fmtDateShort(v.started_at)}</p><p className="text-xs text-[#697386]">{fmtTime(v.started_at)} GST</p></td>
      <td className="px-4 py-3.5"><p className="text-[#172033]">{flag(v.country_code)} {v.country}</p>{v.region && <p className="text-xs text-[#697386]">{v.region}</p>}</td>
      <td className="px-4 py-3.5 whitespace-nowrap font-medium">{fmtDuration(v.duration)}</td>
      <td className="px-4 py-3.5">{v.source}</td>
      <td className="px-4 py-3.5 whitespace-nowrap"><span className="flex items-center gap-2"><Icon size={14} className="text-[#697386]" />{v.device}</span><p className="text-xs text-[#697386]">{v.browser} · {v.os}</p></td>
      <td className="px-4 py-3.5"><p>{v.max_scroll}%</p><p className="text-xs text-[#697386]">{v.sections.map((s) => SECTION_LABELS[s] || s).join(", ") || "Top only"}</p></td>
      <td className="px-4 py-3.5 whitespace-nowrap">{v.visit_number > 1 ? `Returning (#${v.visit_number})` : "New"}</td>
      <td className="px-4 py-3.5">
        {v.events.includes("booked") ? <span className="rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 px-2.5 py-0.5 text-xs">Booked</span>
          : v.events.includes("booking_started") ? <span className="rounded-full bg-[#C7A76C]/15 text-[#7A5F2E] border border-[#C7A76C]/40 px-2.5 py-0.5 text-xs">Started booking</span>
            : <span className="text-xs text-[#697386]">Browsed</span>}
      </td>
    </tr>
  );
}

export default function Visitors() {
  const [days, setDays] = useState("7");
  const [country, setCountry] = useState(null);
  const [page, setPage] = useState(1);
  const [summary, setSummary] = useState(null);
  const [list, setList] = useState(null);
  const [error, setError] = useState(null);
  const [clientErrors, setClientErrors] = useState([]);

  useEffect(() => {
    api.get("/admin/visitors/errors").then((r) => setClientErrors(r.data)).catch(() => {});
  }, []);

  const load = useCallback(() => {
    const fail = (e) => setError(formatApiError(e));
    api.get("/admin/visitors/summary", { params: { days } }).then((r) => { setSummary(r.data); setError(null); }).catch(fail);
    api.get("/admin/visitors", { params: { days, country: country || undefined, page, page_size: PAGE_SIZE } }).then((r) => setList(r.data)).catch(fail);
  }, [days, country, page]);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const pages = list ? Math.max(1, Math.ceil(list.total / PAGE_SIZE)) : 1;
  const pick = (v) => { setDays(v); setPage(1); };

  return (
    <div data-testid="admin-visitors">
      <PageHeader title="Visitors" subtitle="Anonymous visit statistics from the public website. Updates every 30 seconds. Your own admin visits are excluded.">
        <Tabs value={days} onValueChange={pick}>
          <TabsList className="rounded-full">{RANGES.map(([v, l]) => <TabsTrigger key={v} value={v} className="rounded-full" data-testid={`visitors-range-${v}`}>{l}</TabsTrigger>)}</TabsList>
        </Tabs>
      </PageHeader>
      {error && <p className="mb-4 text-sm text-[#C2410C]" role="alert" data-testid="visitors-error">{error}</p>}
      <Stats s={summary} />
      <div className="mt-6 grid md:grid-cols-3 gap-4">
        <TopList title="Top countries" rows={summary?.countries || []} tid="visitors-top-countries" onPick={(c) => { setCountry(c); setPage(1); }} />
        <TopList title="Traffic sources" rows={summary?.sources || []} tid="visitors-top-sources" />
        <TopList title="Devices" rows={summary?.devices || []} tid="visitors-devices" />
      </div>
      <div className="mt-8 flex items-center justify-between">
        <h2 className="font-display text-lg font-semibold text-[#172033]">Recent visits {list ? `(${list.total})` : ""}</h2>
        {country && <button onClick={() => setCountry(null)} className="text-sm rounded-full border bg-white px-3 py-1" data-testid="visitors-clear-country">{country} ✕</button>}
      </div>
      <div className="mt-4 glass rounded-3xl overflow-x-auto">
        <table className="w-full text-sm min-w-[1000px]" data-testid="visitors-table">
          <thead>
            <tr className="text-left text-xs text-[#697386] border-b border-[rgba(20,35,55,0.10)]">
              {["Visited", "Location (approx.)", "Time on site", "Source", "Device", "Scrolled / sections", "Visitor", "Outcome"].map((h) => <th key={h} className="px-4 py-3.5 font-medium whitespace-nowrap">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {list?.items.length === 0 && <tr><td colSpan={8} className="px-4 py-12 text-center text-[#697386]" data-testid="visitors-empty">No visits in this period yet.</td></tr>}
            {list?.items.map((v) => <VisitRow key={v.session_id} v={v} />)}
          </tbody>
        </table>
      </div>
      <div className="mt-5 flex items-center justify-end gap-3 text-sm text-[#697386]">
        <span data-testid="visitors-page-info">Page {page} of {pages}</span>
        <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="h-9 w-9 grid place-items-center rounded-full border bg-white disabled:opacity-30" aria-label="Previous page" data-testid="visitors-prev-page"><ChevronLeft size={16} /></button>
        <button disabled={page >= pages} onClick={() => setPage(page + 1)} className="h-9 w-9 grid place-items-center rounded-full border bg-white disabled:opacity-30" aria-label="Next page" data-testid="visitors-next-page"><ChevronRight size={16} /></button>
      </div>
      <div className="mt-10 glass rounded-3xl p-5" data-testid="visitors-client-errors">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#697386]">Page loading problems reported by visitors' browsers</p>
        {clientErrors.length === 0 ? <p className="mt-3 text-sm text-[#697386]">None reported.</p> : (
          <ul className="mt-3 space-y-2 text-sm">
            {clientErrors.map((e, i) => (
              <li key={i} className="border-b border-[rgba(20,35,55,0.06)] pb-2">
                <span className="text-[#697386]">{fmtDateShort(e.at)} {fmtTime(e.at)} · {e.device} · {e.browser} · {e.os}</span>
                <p className="text-[#172033] break-words">{e.message}</p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
